const UI_SURFACE_BOOTSTRAP_PATH = '/api/ui-surface/bootstrap';
const UI_SURFACE_PREFERENCE_PATH = '/api/ui-surface/preference';
const VALID_SURFACES = new Set(['new', 'legacy']);
const VALID_PREFERENCES = new Set(['default', 'new', 'legacy']);
const VALID_SELECTION_MODES = new Set(['choice_open', 'new_primary']);
// 2026-08-18: role='admin'（本家管理者、client_id紐付け済み）は新UI対象。
// クライアント側の強制legacyはadmin_staffのみに縮小（サーバー判定が正）。
const SERVICE_ADMIN_ROLES = new Set(['admin_staff']);
const SAFE_LEGACY_ROUTE = '/app/mypage';

const safeAppRoute = (value, fallback) => {
  const route = String(value || '').trim();
  return route.startsWith('/app/') && !route.startsWith('//') ? route : fallback;
};

export const createSafeLegacySurfaceDecision = (reason = 'bootstrap-unavailable') => ({
  effectiveSurface: 'legacy',
  reason,
  preference: 'default',
  releaseId: null,
  rollbackAvailable: false,
  routes: {
    legacy: SAFE_LEGACY_ROUTE,
    new: null,
  },
  choice: {
    available: false,
    guaranteedUntil: null,
    deadline: null,
    selectionMode: null,
  },
  safeFallback: true,
});

export const normalizeUiSurfaceDecision = (payload, authenticatedUser) => {
  if (!payload || typeof payload !== 'object') return createSafeLegacySurfaceDecision('invalid-bootstrap');
  const role = String(authenticatedUser?.role || '').trim();
  const surface = String(payload.effectiveSurface || '').trim();
  const preference = String(payload.preference || 'default').trim();
  const legacyRoute = safeAppRoute(payload.routes?.legacy, SAFE_LEGACY_ROUTE);
  const newRoute = safeAppRoute(payload.routes?.new, null);
  const guaranteedUntil = typeof payload.choice?.guaranteedUntil === 'string'
    ? payload.choice.guaranteedUntil
    : (typeof payload.choice?.deadline === 'string' ? payload.choice.deadline : null);
  const selectionMode = VALID_SELECTION_MODES.has(payload.choice?.selectionMode)
    ? payload.choice.selectionMode
    : 'choice_open';

  if (SERVICE_ADMIN_ROLES.has(role)) {
    return {
      ...createSafeLegacySurfaceDecision('service-admin-always-legacy'),
      releaseId: typeof payload.releaseId === 'string' ? payload.releaseId : null,
      routes: { legacy: legacyRoute, new: newRoute },
    };
  }
  if (!VALID_SURFACES.has(surface) || !VALID_PREFERENCES.has(preference)) {
    return {
      ...createSafeLegacySurfaceDecision('invalid-bootstrap'),
      routes: { legacy: legacyRoute, new: null },
    };
  }
  if (surface === 'new' && !newRoute) {
    return {
      ...createSafeLegacySurfaceDecision('missing-new-route'),
      routes: { legacy: legacyRoute, new: null },
    };
  }

  return {
    effectiveSurface: surface,
    reason: String(payload.reason || 'server-decision'),
    preference,
    releaseId: typeof payload.releaseId === 'string' ? payload.releaseId : null,
    rollbackAvailable: payload.rollbackAvailable === true,
    routes: { legacy: legacyRoute, new: newRoute },
    choice: {
      available: payload.choice?.available === true,
      guaranteedUntil,
      deadline: guaranteedUntil,
      selectionMode,
    },
    safeFallback: false,
  };
};

export const fetchUiSurfaceBootstrap = async ({
  authenticatedUser,
  authHeader,
  fetchImpl = null,
  timeoutMs = 5_000,
} = {}) => {
  const request = fetchImpl || (typeof fetch === 'function' ? fetch : null);
  if (typeof request !== 'function') return createSafeLegacySurfaceDecision('fetch-unavailable');
  if (!authenticatedUser?.id || !authHeader) return createSafeLegacySurfaceDecision('authentication-unavailable');

  const controller = typeof AbortController === 'function' ? new AbortController() : null;
  const timeout = controller ? setTimeout(() => controller.abort(), timeoutMs) : null;
  try {
    const response = await request(UI_SURFACE_BOOTSTRAP_PATH, {
      method: 'GET',
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
        Authorization: authHeader,
      },
      ...(controller ? { signal: controller.signal } : {}),
    });
    if (!response?.ok) return createSafeLegacySurfaceDecision(`bootstrap-http-${response?.status || 'unknown'}`);
    try {
      return normalizeUiSurfaceDecision(await response.json(), authenticatedUser);
    } catch (_error) {
      return createSafeLegacySurfaceDecision('invalid-bootstrap');
    }
  } catch (_error) {
    return createSafeLegacySurfaceDecision('bootstrap-request-failed');
  } finally {
    if (timeout) clearTimeout(timeout);
  }
};

export const saveUiSurfacePreference = async ({
  preference,
  authenticatedUser,
  authHeader,
  fetchImpl = null,
  timeoutMs = 5_000,
} = {}) => {
  const request = fetchImpl || (typeof fetch === 'function' ? fetch : null);
  if (typeof request !== 'function') return createSafeLegacySurfaceDecision('fetch-unavailable');
  if (!authenticatedUser?.id || !authHeader) return createSafeLegacySurfaceDecision('authentication-unavailable');
  if (!VALID_PREFERENCES.has(preference)) return createSafeLegacySurfaceDecision('invalid-preference');

  const controller = typeof AbortController === 'function' ? new AbortController() : null;
  const timeout = controller ? setTimeout(() => controller.abort(), timeoutMs) : null;
  let response;
  try {
    response = await request(UI_SURFACE_PREFERENCE_PATH, {
      method: 'PUT',
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
        Authorization: authHeader,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ preference }),
      ...(controller ? { signal: controller.signal } : {}),
    });
  } catch (_error) {
    return createSafeLegacySurfaceDecision('preference-request-failed');
  } finally {
    if (timeout) clearTimeout(timeout);
  }
  if (!response?.ok) return createSafeLegacySurfaceDecision(`preference-http-${response?.status || 'unknown'}`);

  // The mutation endpoint confirms the write, but the bootstrap endpoint owns
  // entry routes and the final server-authored decision. Reload it instead of
  // reconstructing a decision from browser state.
  return fetchUiSurfaceBootstrap({ authenticatedUser, authHeader, fetchImpl: request, timeoutMs });
};

export const getUiSurfaceEntryRoute = (decision) => (
  decision?.effectiveSurface === 'new' && decision?.routes?.new
    ? decision.routes.new
    : safeAppRoute(decision?.routes?.legacy, SAFE_LEGACY_ROUTE)
);

export { SAFE_LEGACY_ROUTE, UI_SURFACE_BOOTSTRAP_PATH, UI_SURFACE_PREFERENCE_PATH };
